/**
 * WorkflowBuilder Page
 *
 * Visual workflow builder with drag-and-drop canvas, step toolbox,
 * and configuration panel.
 */
import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from '../components/ui/card';
import { StepToolbox, StepType } from '../components/workflow/StepToolbox';
import { WorkflowCanvas, CanvasStep } from '../components/workflow/WorkflowCanvas';
import { SchemaForm } from '@/components/schema-form/SchemaForm';
import { useAgent } from '@/hooks';
import { initialValue, isRenderableSchema, pruneEmpty } from '@/types/inputSchema';
import { BuilderIntroModal } from '../components/workflow/BuilderIntroModal';
import { ConfigurationPanel } from '../components/workflow/ConfigurationPanel';
import {
  useWorkflow,
  useCreateWorkflow,
  useUpdateWorkflow,
  getExecutionModeIcon,
  getExecutionModeLabel,
  getTriggerTypeIcon,
  getTriggerTypeLabel,
} from '../hooks/useWorkflows';
import { WorkflowExecutionMode, WorkflowTriggerType } from '../types/workflow';
import { AgentDetailSkeleton } from '../components/common/LoadingSkeleton';
import {
  useSchedules,
  useCreateSchedule,
  useUpdateSchedule,
  useDeleteSchedule,
} from '../hooks/useSchedules';
import { useTriggers, useCreateTrigger, useDeleteTrigger } from '../hooks/useTriggers';
import toast from 'react-hot-toast';
import { useIsPro } from '../hooks/useIsPro';

/**
 * Generate a unique ID for new steps
 */
function generateStepId(): string {
  return `step-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Get icon for step type
 */
function getStepIcon(stepType: string): string {
  switch (stepType) {
    case 'condition':
      return '🔀';
    case 'delay':
      return '⏱️';
    case 'transform':
      return '🔄';
    default:
      return '🤖';
  }
}

// Plain-language explanations of the concepts users stumble on, shown inline.
const EXECUTION_MODE_HELP: Record<string, string> = {
  SEQUENTIAL: 'Runs steps one after another. (Free)',
  PARALLEL: 'Runs steps at the same time on the same input. Pro.',
  CONDITIONAL: 'Branches on a step’s result (if/else). Pro.',
  FAN_OUT: 'Runs the first step once per item in your input’s “items” list. Pro.',
  LOOP: 'Repeats until a condition or max iterations. Pro.',
};
const TRIGGER_TYPE_HELP: Record<string, string> = {
  MANUAL: 'You start each run with the Run button.',
  SCHEDULED: 'Runs automatically on a cron schedule — created when you save.',
  WEBHOOK: 'Runs when a system POSTs to a unique URL — created when you save.',
};

/**
 * WorkflowBuilder Page Component
 */
export default function WorkflowBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  // Workflow data
  const { data: existingWorkflow, isLoading: isLoadingWorkflow, error: loadError } = useWorkflow(id);
  const createWorkflow = useCreateWorkflow();
  const updateWorkflow = useUpdateWorkflow();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [executionMode, setExecutionMode] = useState<WorkflowExecutionMode>('SEQUENTIAL');
  const [triggerType, setTriggerType] = useState<WorkflowTriggerType>('MANUAL');
  const [triggerConfig, setTriggerConfig] = useState<Record<string, unknown>>({});
  const [tags, setTags] = useState('');
  const [maxIterations, setMaxIterations] = useState(3);
  // Automation (the Trigger dropdown wires these to real schedule/webhook records).
  const [cron, setCron] = useState('0 9 * * *');
  const [scheduleInput, setScheduleInput] = useState('');
  const [hookUrl, setHookUrl] = useState('');
  const { data: schedules } = useSchedules();
  const { data: triggers } = useTriggers();
  const createSchedule = useCreateSchedule();
  const updateSchedule = useUpdateSchedule();
  const deleteSchedule = useDeleteSchedule();
  const createTrigger = useCreateTrigger();
  const deleteTrigger = useDeleteTrigger();
  const existingSchedule = schedules?.find((s) => s.workflowId === id);
  const existingTrigger = triggers?.find((t) => t.workflowId === id);

  // Canvas state
  const [steps, setSteps] = useState<CanvasStep[]>([]);
  const [scheduleForm, setScheduleForm] = useState<Record<string, unknown>>({});
  const [scheduleJsonMode, setScheduleJsonMode] = useState(false);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Settings panel visibility
  const [showSettings, setShowSettings] = useState(false);

  // Pro entitlement — flag gated modes and give free users a way to pay.
  const { isPro } = useIsPro();

  // First-run builder intro — shown once (localStorage), re-openable from the header.
  const [showIntro, setShowIntro] = useState(false);
  useEffect(() => {
    if (!isEditMode && !localStorage.getItem('wf-builder-intro-seen')) {
      setShowIntro(true);
    }
  }, [isEditMode]);
  function dismissIntro() {
    localStorage.setItem('wf-builder-intro-seen', '1');
    setShowIntro(false);
  }

  // Initialize form from existing workflow
  useEffect(() => {
    if (existingWorkflow) {
      setName(existingWorkflow.name);
      setDescription(existingWorkflow.description || '');
      setExecutionMode(existingWorkflow.executionMode);
      setTriggerConfig(existingWorkflow.trigger.config);
      setTags(existingWorkflow.tags.join(', '));

      // Convert workflow steps to canvas steps
      const canvasSteps: CanvasStep[] = existingWorkflow.steps.map((step) => {
        const stepType = step.type || (step.agentId ? 'agent' : 'agent');
        return {
          ...step,
          stepType,
          icon:
            stepType === 'agent'
              ? step.agentIcon || '🤖'
              : getStepIcon(stepType),
        };
      });
      setSteps(canvasSteps);
    }
  }, [existingWorkflow]);

  // In edit mode, reflect the workflow's actual automation (a real schedule or
  // webhook trigger record) in the Trigger dropdown.
  useEffect(() => {
    if (!isEditMode) return;
    if (existingSchedule) {
      setTriggerType('SCHEDULED');
      setCron(existingSchedule.cron);
      setScheduleInput(
        existingSchedule.inputData
          ? JSON.stringify(existingSchedule.inputData, null, 2)
          : ''
      );
    } else if (existingTrigger) {
      setTriggerType('WEBHOOK');
      setHookUrl(existingTrigger.hookUrl);
    }
  }, [isEditMode, existingSchedule, existingTrigger]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    // Check if dropping from toolbox
    const activeData = active.data.current;
    if (activeData?.type === 'toolbox-item') {
      const step = activeData.step as StepType;

      // Create new canvas step
      const newStep: CanvasStep = {
        id: generateStepId(),
        name: step.label,
        agentId: step.agentId || '',
        agentName: step.agentName || step.label,
        order: steps.length,
        config: {},
        stepType: step.type,
        icon: step.icon,
      };

      setSteps((prev) => [...prev, newStep]);
      setSelectedStepId(newStep.id);
      return;
    }

    // Handle reordering within canvas
    if (active.id !== over.id) {
      setSteps((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        if (oldIndex === -1 || newIndex === -1) return items;

        const newItems = arrayMove(items, oldIndex, newIndex);
        // Update order property
        return newItems.map((item, index) => ({ ...item, order: index }));
      });
    }
  }, [steps.length]);

  // Update a step
  const handleUpdateStep = useCallback(
    (stepId: string, updates: Partial<CanvasStep>) => {
      setSteps((prev) =>
        prev.map((step) =>
          step.id === stepId ? { ...step, ...updates } : step
        )
      );
    },
    []
  );

  // Remove a step
  const handleRemoveStep = useCallback((stepId: string) => {
    setSteps((prev) => prev.filter((step) => step.id !== stepId));
    if (selectedStepId === stepId) {
      setSelectedStepId(null);
    }
  }, [selectedStepId]);

  // Get selected step
  const selectedStep = steps.find((s) => s.id === selectedStepId) || null;

  // The scheduled input is handed to the workflow's first step, so that step's
  // agent declares its shape. Read straight off the canvas — no extra fetch.
  const firstAgentId = steps
    .slice()
    .sort((a, b) => a.order - b.order)
    .find((step) => step.stepType === 'agent')?.agentId;
  const { data: firstAgent } = useAgent(
    triggerType === 'SCHEDULED' ? firstAgentId : undefined
  );
  const scheduleSchema = firstAgent?.inputSchema;
  const scheduleHasForm = isRenderableSchema(scheduleSchema);
  const scheduleShowingForm = scheduleHasForm && !scheduleJsonMode;

  // Seed once the schema arrives, layering over anything already loaded from an
  // existing schedule so editing a saved workflow keeps its values.
  useEffect(() => {
    if (isRenderableSchema(scheduleSchema)) {
      let existing: Record<string, unknown> | null = null;
      if (scheduleInput.trim()) {
        try {
          existing = JSON.parse(scheduleInput);
        } catch {
          existing = null; // hand-written JSON that no longer parses
        }
      }
      setScheduleForm(initialValue(scheduleSchema, existing));
    }
    // scheduleInput is intentionally omitted: re-seeding on every keystroke
    // would fight the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleSchema]);

  // Handle form submission
  const handleSubmit = async () => {
    // Validate the optional scheduled-run input JSON before doing anything else.
    let scheduleInputData: Record<string, unknown> | undefined;
    if (triggerType === 'SCHEDULED' && scheduleShowingForm) {
      scheduleInputData = pruneEmpty(scheduleForm) as Record<string, unknown> | undefined;
    } else if (triggerType === 'SCHEDULED' && scheduleInput.trim()) {
      try {
        const parsed = JSON.parse(scheduleInput);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          throw new Error('not an object');
        }
        scheduleInputData = parsed as Record<string, unknown>;
      } catch {
        toast.error('Schedule input must be a valid JSON object.');
        return;
      }
    }

    // Convert canvas steps to workflow steps format expected by backend
    const workflowSteps = steps.map((step, index) => ({
      stepId: step.id || `step-${index + 1}`,
      type: step.stepType || 'agent',
      agentId: step.stepType && step.stepType !== 'agent' ? undefined : step.agentId,
      name: step.name,
      order: step.order,
      config: step.config || {},
      dependsOn: index > 0 ? [steps[index - 1].id || `step-${index}`] : [],
      timeoutSeconds: step.timeoutSeconds || 300,
      maxRetries: step.maxRetries || 3,
    }));

    // Build payload matching backend CreateWorkflowRequest schema
    const payload = {
      name,
      description: description || undefined,
      steps: {
        workflowType: executionMode,
        steps: workflowSteps,
        config: {
          trigger: {
            type: triggerType,
            config: triggerConfig,
          },
          tags: tags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
          // Loop settings are read by the backend loop executor.
          ...(executionMode === 'LOOP'
            ? { loop: { max_iterations: maxIterations } }
            : {}),
        },
      },
    };

    try {
      // 1. Save the workflow itself.
      let wfId = id ?? '';
      if (isEditMode && id) {
        await updateWorkflow.mutateAsync({ id, data: payload });
      } else {
        const created = await createWorkflow.mutateAsync(payload);
        wfId = created.id;
      }

      // 2. Reconcile the real automation to match the Trigger choice. The
      //    workflow's own record is already saved, so any automation error just
      //    surfaces a toast — we still continue to the workflow.
      try {
        if (triggerType === 'SCHEDULED') {
          if (existingTrigger) await deleteTrigger.mutateAsync(existingTrigger.id);
          if (existingSchedule) {
            await updateSchedule.mutateAsync({
              id: existingSchedule.id,
              data: { cron, inputData: scheduleInputData },
            });
          } else {
            await createSchedule.mutateAsync({
              workflowId: wfId,
              cron,
              inputData: scheduleInputData,
            });
          }
        } else if (triggerType === 'WEBHOOK') {
          if (existingSchedule) await deleteSchedule.mutateAsync(existingSchedule.id);
          if (!existingTrigger) await createTrigger.mutateAsync(wfId);
        } else {
          // MANUAL — remove any automation that was previously attached.
          if (existingSchedule) await deleteSchedule.mutateAsync(existingSchedule.id);
          if (existingTrigger) await deleteTrigger.mutateAsync(existingTrigger.id);
        }
      } catch {
        // Automation error is shown by the schedule/trigger mutation toast.
      }

      navigate(`/workflows/${wfId}`);
    } catch (err) {
      // The mutation toasts the message, but the Pro gate needs a way to PAY —
      // otherwise a free user reads "requires Pro" and has no path forward.
      const msg = String(
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ??
          (err as Error)?.message ??
          ''
      );
      if (/\bPro\b/i.test(msg)) {
        toast(
          (t) => (
            <span className="text-sm">
              {msg}{' '}
              <button
                type="button"
                className="ml-1 font-semibold underline"
                onClick={() => {
                  toast.dismiss(t.id);
                  navigate('/billing');
                }}
              >
                Upgrade — $29/mo
              </button>
            </span>
          ),
          { duration: 9000, icon: '🔒' }
        );
      }
    }
  };

  // Loading state for edit mode
  if (isEditMode && isLoadingWorkflow) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-7xl mx-auto py-8">
          <AgentDetailSkeleton />
        </div>
      </div>
    );
  }

  // Error state for edit mode
  if (isEditMode && (loadError || !existingWorkflow)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-7xl mx-auto py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-6xl mb-4">❌</div>
              <CardTitle className="mb-2">Workflow Not Found</CardTitle>
              <CardDescription className="mb-4">
                The workflow you're trying to edit doesn't exist or has been
                deleted.
              </CardDescription>
              <Button onClick={() => navigate('/workflows')}>
                Back to Workflows
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const isPending = createWorkflow.isPending || updateWorkflow.isPending;

  return (
    <div className="h-screen flex flex-col bg-slate-100">
      <BuilderIntroModal isOpen={showIntro} onClose={dismissIntro} />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/workflows"
            className="text-sm text-primary hover:underline"
          >
            ← Back to Workflows
          </Link>
          <div className="h-6 w-px bg-slate-200" />
          <h1 className="text-lg font-semibold">
            {isEditMode ? 'Edit Workflow' : 'Create Workflow'}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setShowIntro(true)}>
            How it works
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowSettings(!showSettings)}
          >
            {showSettings ? 'Hide Settings' : 'Settings'}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending || !name.trim() || steps.length === 0}
          >
            {isPending
              ? 'Saving...'
              : isEditMode
              ? 'Update Workflow'
              : 'Create Workflow'}
          </Button>
        </div>
      </header>

      {/* Settings Panel (collapsible) */}
      {showSettings && (
        <div className="bg-white border-b border-slate-200 px-4 py-3">
          <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2">
            <div className="space-y-2">
              <Label htmlFor="workflow-name">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="workflow-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Workflow"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="execution-mode">Execution Mode</Label>
              <Select
                value={executionMode}
                onValueChange={(v) => setExecutionMode(v as WorkflowExecutionMode)}
              >
                <SelectTrigger id="execution-mode">
                  <SelectValue>
                    {getExecutionModeIcon(executionMode)}{' '}
                    {getExecutionModeLabel(executionMode)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SEQUENTIAL">
                    ➡️ Sequential
                  </SelectItem>
                  <SelectItem value="PARALLEL">
                    ⚡ Parallel{!isPro && ' · Pro'}
                  </SelectItem>
                  <SelectItem value="CONDITIONAL">
                    🔀 Conditional{!isPro && ' · Pro'}
                  </SelectItem>
                  <SelectItem value="FAN_OUT">📤 Fan-out{!isPro && ' · Pro'}</SelectItem>
                  <SelectItem value="LOOP">🔁 Loop{!isPro && ' · Pro'}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {EXECUTION_MODE_HELP[executionMode]}
              </p>
              {!isPro && executionMode !== 'SEQUENTIAL' && (
                <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <strong>{getExecutionModeLabel(executionMode)} needs Pro.</strong> Sequential
                  workflows are free.{' '}
                  <Link to="/billing" className="font-semibold underline">
                    Upgrade — $29/mo
                  </Link>
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="trigger-type">Trigger</Label>
              <Select
                value={triggerType}
                onValueChange={(v) => setTriggerType(v as WorkflowTriggerType)}
              >
                <SelectTrigger id="trigger-type">
                  <SelectValue>
                    {getTriggerTypeIcon(triggerType)} {getTriggerTypeLabel(triggerType)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MANUAL">👆 Manual</SelectItem>
                  <SelectItem value="SCHEDULED">⏰ Scheduled</SelectItem>
                  <SelectItem value="WEBHOOK">🔗 Webhook</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {TRIGGER_TYPE_HELP[triggerType]}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tags">Tags</Label>
              <Input
                id="tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="tag1, tag2, tag3"
              />
            </div>
            <div className="space-y-2 md:col-span-2 lg:col-span-4">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this workflow does..."
                rows={2}
                className="min-h-[40px]"
              />
            </div>

            {/* Loop config */}
            {executionMode === 'LOOP' && (
              <div className="space-y-2">
                <Label htmlFor="max-iterations">Max iterations</Label>
                <Input
                  id="max-iterations"
                  type="number"
                  min={1}
                  max={25}
                  value={maxIterations}
                  onChange={(e) =>
                    setMaxIterations(
                      Math.max(1, Math.min(25, Number(e.target.value) || 1))
                    )
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Repeats the steps up to this many times (max 25).
                </p>
              </div>
            )}

            {/* Fan-out needs an items array in the run input */}
            {executionMode === 'FAN_OUT' && (
              <div className="md:col-span-2 lg:col-span-4">
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <p className="font-medium">
                    Fan-out runs the <b>first step</b> once per item, in parallel.
                  </p>
                  <p className="mt-1">
                    Give the run an <code>items</code> array as input — for example:
                  </p>
                  <pre className="mt-1 overflow-x-auto rounded bg-white/70 p-2 font-mono">
                    {'{ "items": ["first", "second", "third"] }'}
                  </pre>
                </div>
              </div>
            )}

            {/* Scheduled trigger: cron expression (creates a real schedule on save) */}
            {triggerType === 'SCHEDULED' && (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="cron">Schedule (cron, UTC)</Label>
                <Input
                  id="cron"
                  value={cron}
                  onChange={(e) => setCron(e.target.value)}
                  placeholder="0 9 * * *"
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  e.g. <code>0 9 * * *</code> = every day at 09:00 UTC. The workflow must be Active
                  for the schedule to run.
                </p>
                <div className="space-y-1 pt-1">
                  <div className="flex items-center justify-between">
                    <Label htmlFor={scheduleShowingForm ? undefined : 'schedule-input'}>
                      {scheduleShowingForm
                        ? 'Input for scheduled runs'
                        : 'Input for scheduled runs (optional JSON)'}
                    </Label>
                    {scheduleHasForm && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-auto py-1 text-xs"
                        onClick={() => {
                          if (!scheduleJsonMode) {
                            setScheduleInput(
                              JSON.stringify(pruneEmpty(scheduleForm) ?? {}, null, 2)
                            );
                          }
                          setScheduleJsonMode((m) => !m);
                        }}
                      >
                        {scheduleJsonMode ? 'Use the form' : 'Edit as JSON'}
                      </Button>
                    )}
                  </div>
                  {scheduleShowingForm ? (
                    <div className="rounded-lg border p-4">
                      <SchemaForm
                        schema={scheduleSchema!}
                        value={scheduleForm}
                        onChange={setScheduleForm}
                      />
                    </div>
                  ) : (
                    <Textarea
                      id="schedule-input"
                      value={scheduleInput}
                      onChange={(e) => setScheduleInput(e.target.value)}
                      placeholder='{ "prompt": "daily report" }'
                      rows={2}
                      className="font-mono text-xs"
                    />
                  )}
                  <p className="text-xs text-muted-foreground">
                    Sent as the workflow&apos;s input each time the schedule fires. Pressing Run
                    starts from these same values, so you can test a scheduled run without
                    waiting for it.
                  </p>
                </div>
              </div>
            )}

            {/* Webhook trigger: the generated hook URL (creates a real trigger on save) */}
            {triggerType === 'WEBHOOK' && (
              <div className="space-y-2 md:col-span-2">
                <Label>Webhook URL</Label>
                {hookUrl ? (
                  <code className="block truncate rounded bg-slate-50 p-2 font-mono text-xs">
                    {hookUrl}
                  </code>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    A unique URL is generated when you save — anything that POSTs to it runs the
                    workflow. Treat it like a password.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {/* Step Toolbox (Left Sidebar) */}
          <StepToolbox />

          {/* Canvas (Center) */}
          <WorkflowCanvas
            steps={steps}
            selectedStepId={selectedStepId}
            onSelectStep={setSelectedStepId}
            onRemoveStep={handleRemoveStep}
            executionMode={executionMode}
          />

          {/* Configuration Panel (Right Sidebar) */}
          <ConfigurationPanel
            step={selectedStep}
            onUpdateStep={handleUpdateStep}
            onClose={() => setSelectedStepId(null)}
          />

          {/* Drag Overlay */}
          <DragOverlay>
            {activeId && activeId.startsWith('toolbox-') && (
              <div className="p-3 bg-white border-2 border-primary rounded-lg shadow-lg opacity-80">
                <p className="text-sm font-medium">Dragging step...</p>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Footer Status Bar */}
      <footer className="bg-white border-t border-slate-200 px-4 py-2 flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center gap-4">
          <span>
            {steps.length} step{steps.length !== 1 ? 's' : ''}
          </span>
          <span>|</span>
          <span>
            {getExecutionModeIcon(executionMode)} {getExecutionModeLabel(executionMode)}
          </span>
          <span>|</span>
          <span>
            {getTriggerTypeIcon(triggerType)} {getTriggerTypeLabel(triggerType)}
          </span>
        </div>
        <div>
          {selectedStepId ? `Editing: ${selectedStep?.name}` : 'Select a step to configure'}
        </div>
      </footer>
    </div>
  );
}
